import { PublicLayout } from '@/layouts/PublicLayout';
import { Button } from '@/components/Button';
import { useNavigate } from 'react-router-dom';

export default function AboutPage() {
  const navigate = useNavigate();

  const values = [
    {
      title: 'Transparency',
      description: 'We believe AI decisions should be explainable. No black boxes, only glass boxes.',
    },
    {
      title: 'Fairness',
      description: 'Every candidate deserves equal opportunity. Our AI is designed to reduce bias, not amplify it.',
    },
    {
      title: 'Compliance',
      description: 'We build for the strictest regulations, ensuring your hiring practices are always compliant.',
    },
    {
      title: 'Human-Centered',
      description: 'AI augments human decision-making but never replaces it. Humans stay in control.',
    },
  ];

  return (
    <PublicLayout>
      {/* Hero */}
      <section className="bg-gradient-to-br from-primary-50 via-white to-secondary-50 py-20">
        <div className="mx-auto max-w-4xl px-4 sm:px-6 lg:px-8 text-center">
          <h1 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
            Making Hiring Fair for Everyone
          </h1>
          <p className="text-xl text-gray-600">
            We're on a mission to bring transparency and fairness to AI-powered recruitment.
          </p>
        </div>
      </section>

      {/* Story */}
      <section className="py-24 bg-white">
        <div className="mx-auto max-w-4xl px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl font-bold text-gray-900 mb-6">Our Story</h2>
          <div className="prose prose-lg max-w-none text-gray-600 space-y-4">
            <p>
            ePeople Agent was born from a simple observation: while AI was transforming recruitment,
            it was doing so in ways that often lacked transparency and could perpetuate bias.
            </p>
            <p>
              We asked ourselves: what if AI could not only make hiring more efficient but also more fair?
              What if every candidate could understand exactly why they were or weren't a match? What if
              employers could prove their hiring practices were bias-free?
            </p>
            <p>
              That's when we created the Glass Box AI approach. Instead of hiding AI decisions behind
              complex algorithms, we made them explainable. Instead of assuming AI is neutral, we actively
              test for and eliminate bias. Instead of replacing human judgment, we augment it.
            </p>
            <p>
              Today, we're proud to help organizations of all sizes hire more fairly, efficiently, and
              compliantly while giving every candidate the transparency they deserve.
            </p>
          </div>
        </div>
      </section>

      {/* Values */}
      <section className="py-24 bg-gray-50">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl font-bold text-gray-900 text-center mb-12">Our Values</h2>
          <div className="grid gap-8 md:grid-cols-2 lg:grid-cols-4">
            {values.map((value) => (
              <div key={value.title} className="bg-white rounded-xl p-6 shadow-sm border border-gray-200">
                <h3 className="text-xl font-semibold text-gray-900 mb-3">{value.title}</h3>
                <p className="text-gray-600">{value.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-24 bg-white">
        <div className="mx-auto max-w-4xl px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6">
            Join Us in Transforming Hiring
          </h2>
          <p className="text-xl text-gray-600 mb-10">
            Be part of the movement towards fair, transparent, and compliant recruitment.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button size="lg" onClick={() => navigate('/signup')}>
              Get Started
            </Button>
            <Button size="lg" variant="outline" onClick={() => navigate('/contact')}>
              Contact Us
            </Button>
          </div>
        </div>
      </section>
    </PublicLayout>
  );
}
